import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import { AppDialogConfirmationProps, AppDialogConfirmationVariant } from "./appDialogConfirmation.types";

function getVariantClasses(variant: AppDialogConfirmationVariant) {
	switch (variant) {
		case "error":
			return {
				badge: "bg-danger/10 text-danger",
				details: "border-danger/25 bg-danger/8 text-foreground",
			};
		case "success":
			return {
				badge: "bg-success/10 text-success",
				details: "border-success/25 bg-success/8 text-foreground",
			};
		case "warning":
			return {
				badge: "bg-warning/10 text-warning",
				details: "border-warning/25 bg-warning/8 text-foreground",
			};
		case "info":
		default:
			return {
				badge: "bg-info/10 text-info",
				details: "border-info/25 bg-info/8 text-foreground",
			};
	}
}

function getIconName(variant: AppDialogConfirmationVariant): string {
	switch (variant) {
		case "error":
			return "lucide:circle-x";
		case "info":
			return "lucide:info";
		case "success":
			return "lucide:circle-check";
		case "warning":
		default:
			return "lucide:triangle-alert";
	}
}

export default function AppDialogConfirmation({
	open,
	variant = "info",
	title,
	message,
	details,
	showDetailsIcon = true,
	confirmText = "Confirmar",
	cancelText,
	showCancel,
	confirmLoading = false,
	disableConfirm = false,
	onConfirm,
	onCancel,
	secondaryText,
	onSecondary,
	disableSecondary = false,
	maxWidth = "xs",
}: Readonly<AppDialogConfirmationProps>) {
	const iconName = getIconName(variant);
	const variantClasses = getVariantClasses(variant);

	const temAcaoSecundaria = Boolean(secondaryText && onSecondary);
	// `cancelText` explícito também exibe o botão: a regra antiga, presa a `warning`, é resíduo de
	// quando o cancelar era vermelho e não podia aparecer junto de dialogs `error`.
	const deveExibirCancelar = showCancel ?? (variant === "warning" || cancelText !== undefined);

	const botaoCancelar = deveExibirCancelar ? (
		<Button
			variant="outlined"
			color="inherit"
			onClick={onCancel}
			fullWidth
			disabled={confirmLoading}
			className={temAcaoSecundaria ? "m-0! basis-full" : undefined}
		>
			{cancelText ?? "Cancelar"}
		</Button>
	) : null;

	return (
		<Dialog
			open={open}
			onClose={(_, reason) => {
				if (confirmLoading) return;
				if (reason === "backdropClick" || reason === "escapeKeyDown") onCancel();
			}}
			maxWidth={maxWidth}
			fullWidth
			disableEscapeKeyDown={confirmLoading}
			keepMounted={false}
			slotProps={{
				transition: { unmountOnExit: true },
				paper: { className: "rounded-2xl" },
			}}
		>
			<DialogContent className="flex flex-col items-center gap-2 px-6 pt-7 pb-2 text-center">
				<div className={`flex h-16 w-16 items-center justify-center rounded-full ${variantClasses.badge}`}>
					<FuseSvgIcon size={32}>{iconName}</FuseSvgIcon>
				</div>

				<h1 className="text-xl font-semibold text-foreground">{title}</h1>
				<p className="text-md leading-relaxed text-muted-foreground">{message}</p>

				{details ? (
					<div
						className={`mt-1 flex w-full items-center rounded-xl border px-3 py-2 text-sm ${
							showDetailsIcon ? "gap-2 text-left" : "justify-center text-center"
						} ${variantClasses.details}`}
					>
						{showDetailsIcon ? (
							<FuseSvgIcon size={18} className="shrink-0">
								{iconName}
							</FuseSvgIcon>
						) : null}
						<div className="min-w-0 flex-1">{details}</div>
					</div>
				) : null}
			</DialogContent>

			{/*
			 * Com ação secundária os dois caminhos de "sim" dividem a primeira linha e o cancelar quebra
			 * para a de baixo (`basis-full`). A ordem do DOM acompanha a visual, para o tab seguir a tela.
			 */}
			<DialogActions className={`gap-2 px-6 pb-6 ${temAcaoSecundaria ? "flex-wrap" : ""}`}>
				{temAcaoSecundaria ? null : botaoCancelar}

				<Button
					autoFocus
					variant="contained"
					color={variant}
					onClick={onConfirm}
					fullWidth
					disabled={disableConfirm || confirmLoading}
					startIcon={confirmLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
					className={temAcaoSecundaria ? "m-0! min-w-0 flex-1 basis-0" : undefined}
				>
					{confirmText}
				</Button>

				{temAcaoSecundaria ? (
					<Button
						variant="outlined"
						color={variant}
						onClick={onSecondary}
						fullWidth
						disabled={disableSecondary || confirmLoading}
						className="m-0! min-w-0 flex-1 basis-0"
					>
						{secondaryText}
					</Button>
				) : null}

				{temAcaoSecundaria ? botaoCancelar : null}
			</DialogActions>
		</Dialog>
	);
}
